import axios from 'axios';
import { apiTimeout } from '../error';

const { REACT_APP_API_URL } = process.env;
const BASE = `${REACT_APP_API_URL}/concierge-web`;
const VISITOR_KEY = 'jadecaps_chat_visitor';
// The assistant may run several lookups before replying.
const REPLY_TIMEOUT_MS = 90000;

const unwrap = (response) => response.data?.data ?? response.data;

function readVisitorToken() {
  try {
    return localStorage.getItem(VISITOR_KEY);
  } catch {
    return null;
  }
}

function storeVisitorToken(token) {
  try {
    localStorage.setItem(VISITOR_KEY, token);
  } catch {
    /* storage blocked — token lives for this page only */
  }
}

let memoryVisitorToken = null;

// Login / logout (customerSession.js) clears the stored token; forget the in-memory copy too, so
// the next anonymous visitor in this browser starts with a fresh identity and none of the old chats.
if (typeof window !== 'undefined') {
  window.addEventListener('jadecaps:auth-changed', () => {
    memoryVisitorToken = null;
  });
}

/** Signed-in guests are identified by the customer token (added by axiosAuth); others by a visitor token. */
async function ensureVisitorToken() {
  const existing = readVisitorToken() || memoryVisitorToken;
  if (existing) return existing;
  const data = unwrap(await apiTimeout(axios.post(`${BASE}/session`)));
  memoryVisitorToken = data.visitorToken;
  storeVisitorToken(data.visitorToken);
  return data.visitorToken;
}

function dropVisitorToken() {
  memoryVisitorToken = null;
  try {
    localStorage.removeItem(VISITOR_KEY);
  } catch {
    /* ignore */
  }
}

async function chatHeaders() {
  return { headers: { 'x-visitor-token': await ensureVisitorToken() } };
}

/** Runs a chat request; if the visitor token has expired (30 days), starts a fresh one and retries once. */
async function withVisitor(request) {
  try {
    return await request(await chatHeaders());
  } catch (err) {
    if (err?.response?.status !== 401) throw err;
    dropVisitorToken();
    return request(await chatHeaders());
  }
}

/** { enabled, assistantName, welcomeMessage } */
export const fetchChatConfig = () => apiTimeout(axios.get(`${BASE}/config`)).then(unwrap);

/** context: { propertyId, checkIn, checkOut, guests } → { conversation, messages } */
export const openChat = async (context) =>
  unwrap(await withVisitor((auth) => apiTimeout(axios.post(`${BASE}/conversations`, context, auth))));

export const fetchChatMessages = async (conversationId, afterId) =>
  unwrap(
    await withVisitor((auth) =>
      apiTimeout(
        axios.get(`${BASE}/conversations/${conversationId}/messages`, {
          ...auth,
          params: afterId ? { afterId } : {},
        })
      )
    )
  );

/** → { messages: [guestMessage, ...replies], conversation } */
/** The guest's website chats, newest first: [{ id, title, lastMessagePreview, lastMessageAt, ... }] */
export const fetchChatList = async () =>
  unwrap(await withVisitor((auth) => apiTimeout(axios.get(`${BASE}/conversations`, auth))));

/** page: the property page the guest is on now ({ propertyId, checkIn, checkOut, guests } or { propertyId: null }) */
export const sendChatMessage = async (conversationId, body, replyToMessageId = null, page = undefined) =>
  unwrap(
    // Not apiTimeout (fixed 30s): a reply with several lookups can take longer.
    await withVisitor((auth) =>
      axios.post(`${BASE}/conversations/${conversationId}/messages`, { body, replyToMessageId, page }, {
        ...auth,
        timeout: REPLY_TIMEOUT_MS,
      })
    )
  );

/** In-chat "share your details" form → { messages: [guestMessage, ...replies], conversation } */
export const shareChatContact = async (conversationId, { name, phone, email }) =>
  unwrap(
    await withVisitor((auth) =>
      axios.post(`${BASE}/conversations/${conversationId}/contact`, { name, phone, email }, {
        ...auth,
        timeout: REPLY_TIMEOUT_MS,
      })
    )
  );
